import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, HelpCircle, Eye, MessageSquare, RotateCcw, ChevronRight, ChevronLeft, Check } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const SQ3R_STEPS = [
  { key: "survey", label: "Survey", icon: Eye, color: "bg-neo-blue", desc: "Skim the paper: read the title, abstract, headings, and conclusion to get a bird's eye view." },
  { key: "question", label: "Question", icon: HelpCircle, color: "bg-neo-yellow", desc: "What do you expect to learn? What questions does the title and abstract raise?" },
  { key: "read", label: "Read", icon: BookOpen, color: "bg-neo-green", desc: "Now read the paper page by page. Take your time and engage with quizzes." },
  { key: "recite", label: "Recite", icon: MessageSquare, color: "bg-neo-purple", desc: "In your own words, summarize what you just read. What were the key points?" },
  { key: "review", label: "Review", icon: RotateCcw, color: "bg-neo-red", desc: "Go back and review your notes, highlights, and quiz results. Fill in any gaps." },
];

export default function SQ3RFlow({ api, paperId, currentStep, onStepChange, onStartReading }) {
  const [sq3rData, setSq3rData] = useState({
    survey_notes: "", questions: [""], recite_notes: "", review_notes: "", current_step: "survey"
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${api}/papers/${paperId}/sq3r`, { withCredentials: true });
        if (res.data) {
          setSq3rData(prev => ({
            ...prev,
            ...res.data,
            questions: res.data.questions?.length ? res.data.questions : [""],
          }));
        }
      } catch { /* ignore */ }
    };
    fetch();
  }, [api, paperId]);

  const save = async (stepOverride) => {
    setSaving(true);
    const step = stepOverride || sq3rData.current_step;
    try {
      await axios.post(`${api}/papers/${paperId}/sq3r`, {
        ...sq3rData,
        questions: sq3rData.questions.filter(q => q.trim()),
        current_step: step,
      }, { withCredentials: true });
    } catch { /* ignore */ }
    setSaving(false);
  };

  const currentIdx = SQ3R_STEPS.findIndex(s => s.key === (currentStep || sq3rData.current_step));

  const goNext = async () => {
    const nextIdx = Math.min(currentIdx + 1, SQ3R_STEPS.length - 1);
    const nextStep = SQ3R_STEPS[nextIdx].key;
    setSq3rData(prev => ({ ...prev, current_step: nextStep }));
    await save(nextStep);
    if (onStepChange) onStepChange(nextStep);
    if (nextStep === "read" && onStartReading) onStartReading();
  };

  const goPrev = async () => {
    const prevIdx = Math.max(currentIdx - 1, 0);
    const prevStep = SQ3R_STEPS[prevIdx].key;
    setSq3rData(prev => ({ ...prev, current_step: prevStep }));
    await save(prevStep);
    if (onStepChange) onStepChange(prevStep);
  };

  const addQuestion = () => setSq3rData(prev => ({ ...prev, questions: [...prev.questions, ""] }));
  const updateQuestion = (idx, val) => setSq3rData(prev => {
    const q = [...prev.questions]; q[idx] = val; return { ...prev, questions: q };
  });

  const step = SQ3R_STEPS[currentIdx] || SQ3R_STEPS[0];
  const StepIcon = step.icon;

  return (
    <div data-testid="sq3r-flow">
      {/* Step Progress */}
      <div className="flex items-center justify-between mb-4 overflow-x-auto pb-2">
        {SQ3R_STEPS.map((s, i) => (
          <div key={s.key} className="flex items-center">
            <div className={`flex flex-col items-center ${i <= currentIdx ? "" : "opacity-40"}`} style={{ minWidth: 60 }}>
              <div className={`w-8 h-8 rounded-full border-2 border-neo-border dark:border-[#444] flex items-center justify-center text-xs font-bold ${i < currentIdx ? "bg-neo-green" : i === currentIdx ? s.color : "bg-gray-200 dark:bg-gray-700"}`}>
                {i < currentIdx ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className="text-[10px] font-heading font-bold mt-1 dark:text-white text-center">{s.label}</span>
            </div>
            {i < SQ3R_STEPS.length - 1 && (
              <div className={`w-6 sm:w-10 h-0.5 mx-0.5 ${i < currentIdx ? "bg-neo-green" : "bg-gray-300 dark:bg-gray-600"}`} />
            )}
          </div>
        ))}
      </div>

      {/* Current Step Content */}
      <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]">
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-lg ${step.color} border-2 border-neo-border dark:border-[#444] flex items-center justify-center`}>
            <StepIcon className="w-4 h-4 text-neo-text" />
          </div>
          <div>
            <h3 className="font-heading font-bold dark:text-white">{step.label}</h3>
            <p className="text-xs text-neo-text-secondary dark:text-gray-400">{step.desc}</p>
          </div>
        </div>

        {/* Survey Step */}
        {step.key === "survey" && (
          <div>
            <Textarea
              value={sq3rData.survey_notes}
              onChange={e => setSq3rData(prev => ({ ...prev, survey_notes: e.target.value }))}
              placeholder="Jot down your first impressions after skimming the title, abstract, headings, and conclusion..."
              className="border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg text-sm min-h-[100px]"
              rows={4}
              data-testid="survey-notes"
            />
          </div>
        )}

        {/* Question Step */}
        {step.key === "question" && (
          <div className="space-y-2">
            {sq3rData.questions.map((q, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs font-bold dark:text-white w-6">Q{i + 1}</span>
                <input
                  value={q}
                  onChange={e => updateQuestion(i, e.target.value)}
                  placeholder="What question does this paper raise for you?"
                  className="flex-1 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg px-3 py-2 text-sm"
                  data-testid={`sq3r-question-${i}`}
                />
              </div>
            ))}
            <button onClick={addQuestion} className="text-xs text-neo-purple hover:underline dark:text-neo-purple" data-testid="add-question-btn">
              + Add another question
            </button>
          </div>
        )}

        {/* Read Step */}
        {step.key === "read" && (
          <div className="text-center py-4">
            <BookOpen className="w-12 h-12 text-neo-green mx-auto mb-2" />
            <p className="font-heading font-bold dark:text-white mb-1">Time to Read!</p>
            <p className="text-xs text-neo-text-secondary dark:text-gray-400">
              Read the paper page by page. Quizzes will appear every 2-3 pages to check your comprehension.
            </p>
          </div>
        )}

        {/* Recite Step */}
        {step.key === "recite" && (
          <div>
            <Textarea
              value={sq3rData.recite_notes}
              onChange={e => setSq3rData(prev => ({ ...prev, recite_notes: e.target.value }))}
              placeholder="In your own words, summarize what you just read. What were the main arguments, findings, and conclusions?"
              className="border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg text-sm min-h-[100px]"
              rows={4}
              data-testid="recite-notes"
            />
          </div>
        )}

        {/* Review Step */}
        {step.key === "review" && (
          <div>
            <Textarea
              value={sq3rData.review_notes}
              onChange={e => setSq3rData(prev => ({ ...prev, review_notes: e.target.value }))}
              placeholder="Review your highlights, notes, and quiz results. What gaps remain? What would you like to revisit?"
              className="border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg text-sm min-h-[100px]"
              rows={4}
              data-testid="review-notes"
            />
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-4 pt-3 border-t-2 border-neo-border/20 dark:border-[#444]/40">
          <button
            onClick={goPrev}
            disabled={currentIdx === 0}
            className={`neo-btn text-xs flex items-center gap-1 ${currentIdx === 0 ? "bg-gray-200 text-gray-400 border-gray-300 shadow-none cursor-not-allowed" : "bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white"}`}
            data-testid="sq3r-prev"
          >
            <ChevronLeft className="w-3 h-3" /> Back
          </button>
          <button
            onClick={goNext}
            className="neo-btn neo-btn-primary text-xs flex items-center gap-1"
            data-testid="sq3r-next"
          >
            {currentIdx === SQ3R_STEPS.length - 1 ? "Complete" : "Next"} <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
